import { Check, Copy, ThumbsDown, ThumbsUp } from "lucide-react";
import { useCallback, useEffect, useRef, useState } from "react";

import { HoverTooltip } from "@/components/common/HoverTooltip";
import { Button } from "@/components/ui/button";
import { cn } from "@/lib/utils";

import type { KaiFeedbackSentiment } from "./kaiFeedback";

interface KaiMessageActionsProps {
  content: string;
  sentiment?: KaiFeedbackSentiment;
  onFeedback?: (sentiment: KaiFeedbackSentiment) => void;
  /** Assistant replies get thumbs up/down; user messages are copy-only. */
  showFeedback?: boolean;
  className?: string;
}

async function copyText(text: string): Promise<boolean> {
  try {
    await navigator.clipboard.writeText(text);
    return true;
  } catch {
    try {
      const ta = document.createElement("textarea");
      ta.value = text;
      ta.setAttribute("readonly", "");
      ta.style.position = "fixed";
      ta.style.left = "-9999px";
      document.body.appendChild(ta);
      ta.select();
      const ok = document.execCommand("copy");
      document.body.removeChild(ta);
      return ok;
    } catch {
      return false;
    }
  }
}

/** Copy + optional thumbs feedback — standard LLM chat affordances. */
export function KaiMessageActions({
  content,
  sentiment,
  onFeedback,
  showFeedback = true,
  className,
}: KaiMessageActionsProps) {
  const [copied, setCopied] = useState(false);
  const copyTimer = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(() => {
    return () => {
      if (copyTimer.current) clearTimeout(copyTimer.current);
    };
  }, []);

  const handleCopy = useCallback(async () => {
    const trimmed = content.trim();
    if (!trimmed) return;
    const ok = await copyText(trimmed);
    if (!ok) return;
    setCopied(true);
    if (copyTimer.current) clearTimeout(copyTimer.current);
    copyTimer.current = setTimeout(() => setCopied(false), 2000);
  }, [content]);

  const hasSubmitted = sentiment != null;
  const helpfulActive = sentiment === "helpful";
  const notHelpfulActive = sentiment === "not_helpful";

  if (!content.trim()) return null;

  return (
    <div
      className={cn(
        "flex items-center gap-0.5",
        className,
      )}
      role="toolbar"
      aria-label="Message actions"
    >
      {showFeedback && onFeedback && (
        <>
          <HoverTooltip content={helpfulActive ? "Marked helpful" : "Helpful"}>
            <Button
              type="button"
              variant="ghost"
              size="iconSm"
              className={cn(
                "size-7 text-muted-foreground hover:text-foreground",
                helpfulActive && "text-primary",
              )}
              aria-label="Helpful"
              aria-pressed={helpfulActive}
              disabled={hasSubmitted && !helpfulActive}
              onClick={() => onFeedback("helpful")}
            >
              <ThumbsUp
                className={cn("size-3.5", helpfulActive && "fill-current")}
              />
            </Button>
          </HoverTooltip>
          <HoverTooltip
            content={notHelpfulActive ? "Marked not helpful" : "Not helpful"}
          >
            <Button
              type="button"
              variant="ghost"
              size="iconSm"
              className={cn(
                "size-7 text-muted-foreground hover:text-foreground",
                notHelpfulActive && "text-primary",
              )}
              aria-label="Not helpful"
              aria-pressed={notHelpfulActive}
              disabled={hasSubmitted && !notHelpfulActive}
              onClick={() => onFeedback("not_helpful")}
            >
              <ThumbsDown
                className={cn(
                  "size-3.5",
                  notHelpfulActive && "fill-current",
                )}
              />
            </Button>
          </HoverTooltip>
        </>
      )}
      <HoverTooltip content={copied ? "Copied" : "Copy"}>
        <Button
          type="button"
          variant="ghost"
          size="iconSm"
          className="size-7 text-muted-foreground hover:text-foreground"
          aria-label={copied ? "Copied" : "Copy message"}
          onClick={() => void handleCopy()}
        >
          {copied ? (
            <Check className="size-3.5 text-primary" />
          ) : (
            <Copy className="size-3.5" />
          )}
        </Button>
      </HoverTooltip>
    </div>
  );
}
